import { c as _c } from "react/compiler-runtime";
import Logo from '@/assets/logos/cs/logo_gcss.svg';
import LogoSkipPay from '@/assets/logos/logo.svg';
import { LINKS, SELF_CARE_LINKS } from '@/constants/links';
import { useTranslation } from 'react-i18next';
import React, { useRef, Suspense, ReactNode, useState, useMemo } from 'react';
import { useClientOnly } from '@/hooks/useClientOnly';
import { mergeClasses } from '@/utils/style';
import { Button } from '@/components/buttons';
import { Link } from '@/components/links';
import { Section } from './useGcssSections';
import { GcssSubsection } from './GcssSubsection';
import UserCircleIcon from '@/assets/icons/heroIcons/User-circle.svg';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import { hasWindow } from '@/utils/dom';
import { useInAppDetect } from '@/hooks/useInAppDetect';
import { ClientGate } from '@/components/ClientGate';
import { useHeader } from '@/sections/layout/Header/useHeader';
import { useGcssData } from '@/sections/gcss/hooks/useGcssData';
const variants = {
  open: () => ({
    x: 0,
    transition: {
      type: 'spring' as const,
      ease: 'easeIn' as const,
      bounce: 0,
      duration: 0.3
    }
  }),
  closed: ({
    isResponsive
  }: VariantProps) => ({
    x: isResponsive ? 0 : '100%',
    transition: {
      type: 'spring' as const,
      ease: 'easeIn' as const,
      bounce: 0,
      duration: 0.3
    }
  })
};
type VariantProps = {
  windowWidth: number;
  isResponsive: boolean;
};
const navButtonsDimensions = 'h-10 2xl:h-12 4xl:h-14 font-medium!';
function ClientNavFallback(t0) {
  const $ = _c(4);
  const {
    headerSections
  } = t0;
  let t1;
  if ($[0] !== headerSections) {
    t1 = headerSections.map(_temp2);
    $[0] = headerSections;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <nav className="h-10 w-10 opacity-0 lg:h-18 lg:w-18">{t1}</nav>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp2(section) {
  return <ul key={section.id}><li>{section.subsection && <><h4>{section.name}</h4><ul>{section.subsection.map(_temp)}</ul></>}</li></ul>;
}
function _temp(subsection) {
  return <li key={subsection.id} />;
}
export function GcssHeader(t0) {
  const $ = _c(85);
  const {
    variant,
    search
  } = t0;
  const ref = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["core", "gcss"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const refList = useRef(null);
  const {
    isClient
  } = useClientOnly();
  const {
    data
  } = useGcssData();
  const headerSections = data?.headerSections;
  const [listScrollProgress, setListScrollProgress] = useState(0);
  const [listScroll, setListScroll] = useState(0);
  const t2 = isClient ? refList : undefined;
  let t3;
  if ($[1] !== t2) {
    t3 = {
      container: t2
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    scrollXProgress,
    scrollX
  } = useScroll(t3);
  useMotionValueEvent(scrollXProgress, "change", setListScrollProgress);
  useMotionValueEvent(scrollX, "change", setListScroll);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      headerRef: ref
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    debouncedOpenedSubmenu,
    isOpenMobileMenu,
    isResponsive,
    isLoggedIn,
    scrolling,
    handleSubsectionOpen,
    handleSubsectionClose,
    setOpenedSubmenu,
    toggleLockScroll
  } = useHeader(t4);
  const isInApp = useInAppDetect();
  let t5;
  if ($[4] !== headerSections) {
    t5 = headerSections ?? [];
    $[4] = headerSections;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <ClientNavFallback headerSections={t5} />;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const fallback = t6;
  if (isInApp) {
    return null;
  }
  const t7 = scrolling ? "shadow-floating bg-white" : "bg-white xl:bg-transparent";
  let t8;
  if ($[8] !== t7) {
    t8 = mergeClasses("fixed z-50 mx-auto flex max-w-[1792px] flex-col gap-3", "transition-all duration-300", "4xl:rounded-2xl 4xl:pl-4 right-0 left-0 px-4 py-2.5 sm:py-3 xl:top-4 xl:right-4 xl:left-4 xl:rounded-xl xl:py-0", t7);
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = LINKS();
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const t10 = t9.gcssHomepage;
  const t11 = variant === "inverted" && !scrolling ? "text-white!" : "text-gray-900!";
  let t12;
  if ($[11] !== t11) {
    t12 = mergeClasses("h-8 transition-colors duration-200 xl:h-20", t11);
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t) {
    t13 = t("core:link.homepage");
    $[13] = t;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Logo className="hidden h-full w-auto xl:block" />;
    t15 = <LogoSkipPay className="h-full w-auto xl:hidden" />;
    $[15] = t14;
    $[16] = t15;
  } else {
    t14 = $[15];
    t15 = $[16];
  }
  let t16;
  if ($[17] !== t12 || $[18] !== t13) {
    t16 = <Link to={t10} analyticsParam="headerHome" testId="headerHome" className={t12} aria-label={t13}>{t14}{t15}</Link>;
    $[17] = t12;
    $[18] = t13;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  const t17 = isResponsive ? "block" : isOpenMobileMenu ? "block" : "hidden";
  let t18;
  if ($[20] !== t17) {
    t18 = mergeClasses("xl-gap-4 3xl:gap-5 4xl:gap-8 hidden gap-4 overflow-hidden xl:flex xl:transform-none! xl:overflow-visible", t17);
    $[20] = t17;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  let t19;
  if ($[22] !== isResponsive) {
    t19 = {
      windowWidth: hasWindow() ? window.innerWidth : 0,
      isResponsive
    };
    $[22] = isResponsive;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  const t20 = isResponsive ? "open" : isOpenMobileMenu ? "open" : "closed";
  let t21;
  if ($[24] !== debouncedOpenedSubmenu || $[25] !== handleSubsectionClose || $[26] !== handleSubsectionOpen || $[27] !== headerSections || $[28] !== scrolling || $[29] !== setOpenedSubmenu || $[30] !== variant) {
    t21 = headerSections?.map(section => <li key={section.id} onMouseEnter={() => handleSubsectionOpen(section.id)} onMouseLeave={handleSubsectionClose} onFocus={() => handleSubsectionOpen(section.id, {
      immediate: true
    })} onBlur={handleSubsectionClose}><Suspense>{section.subsection && <GcssSubsection isOpen={debouncedOpenedSubmenu === section.id} handleClick={() => {
          setOpenedSubmenu(value => {
            if (value === section.id) {
              return null;
            }
            return section.id;
          });
        }} sectionName={section.name} sectionId={section.id} subsection={section.subsection} isScrolling={scrolling} isSectionActive={false} variant={variant} />}</Suspense></li>);
    $[24] = debouncedOpenedSubmenu;
    $[25] = handleSubsectionClose;
    $[26] = handleSubsectionOpen;
    $[27] = headerSections;
    $[28] = scrolling;
    $[29] = setOpenedSubmenu;
    $[30] = variant;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== search) {
    t22 = search && <li><div className="3xl:w-72 flex h-full w-50 flex-col items-center justify-center 2xl:w-60">{search}</div></li>;
    $[32] = search;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== t21 || $[35] !== t22) {
    t23 = <ul className="4xl:gap-8 hidden h-[calc(100%-14rem)] flex-col overflow-x-auto xl:flex xl:flex-row xl:items-center xl:gap-4 xl:overflow-visible xl:pb-0 2xl:gap-6">{t21}{t22}</ul>;
    $[34] = t21;
    $[35] = t22;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t18 || $[38] !== t19 || $[39] !== t20 || $[40] !== t23 || $[41] !== toggleLockScroll) {
    t24 = <motion.nav className={t18} variants={variants} custom={t19} animate={t20} initial="closed" onAnimationComplete={toggleLockScroll}>{t23}</motion.nav>;
    $[37] = t18;
    $[38] = t19;
    $[39] = t20;
    $[40] = t23;
    $[41] = toggleLockScroll;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== fallback || $[44] !== t24) {
    t25 = <ClientGate fallback={fallback}>{t24}</ClientGate>;
    $[43] = fallback;
    $[44] = t24;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== isLoggedIn || $[47] !== t) {
    t26 = <div className="ml-auto hidden items-center space-x-3 xl:flex">{isLoggedIn ? <Button variant="primary" analyticsParam="selfCareOverview" testId="selfCareOverview" to={SELF_CARE_LINKS.overview} className={mergeClasses("w-[15.5rem]!", navButtonsDimensions)} leftIcon={<UserCircleIcon className="-my-2 h-7 w-7" />}>{t("header.loggedIn.button")}</Button> : <><Button variant="primary" analyticsParam="headerRegister" testId="headerRegister" to={LINKS().registration} className={mergeClasses("3xl:w-50 xl:w-32 2xl:w-36", navButtonsDimensions)}>{t("gcss:header.register.button")}</Button></>}</div>;
    $[46] = isLoggedIn;
    $[47] = t;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = mergeClasses("ml-auto flex w-32 justify-center px-4 py-2 xl:hidden", navButtonsDimensions);
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = LINKS();
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  const t29 = t28.registration;
  let t30;
  if ($[51] !== t) {
    t30 = t("gcss:header.register.button");
    $[51] = t;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== t30) {
    t31 = <Button className={t27} variant="primary" analyticsParam="headerMobileRegister" testId="headerMobileRegister" to={t29}>{t30}</Button>;
    $[53] = t30;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] !== t16 || $[56] !== t25 || $[57] !== t26 || $[58] !== t31) {
    t32 = <div className="4xl:gap-16 flex items-center gap-6 2xl:gap-4">{t16}{t25}{t26}{t31}</div>;
    $[55] = t16;
    $[56] = t25;
    $[57] = t26;
    $[58] = t31;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] !== search) {
    t33 = search && <div className="w-full xl:hidden">{search}</div>;
    $[60] = search;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  const t34 = listScroll === 0 ? 0 : listScrollProgress / 0.1;
  let t35;
  if ($[62] !== t34) {
    t35 = <div className="pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-16 bg-linear-to-r from-white to-transparent" style={{
      opacity: t34
    }} />;
    $[62] = t34;
    $[63] = t35;
  } else {
    t35 = $[63];
  }
  const t36 = (1 - listScrollProgress) / 0.1;
  let t37;
  if ($[64] !== t36) {
    t37 = <div className="pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-16 bg-linear-to-l from-white to-transparent" style={{
      opacity: t36
    }} />;
    $[64] = t36;
    $[65] = t37;
  } else {
    t37 = $[65];
  }
  let t38;
  if ($[66] !== debouncedOpenedSubmenu || $[67] !== handleSubsectionClose || $[68] !== handleSubsectionOpen || $[69] !== headerSections || $[70] !== scrolling || $[71] !== setOpenedSubmenu || $[72] !== variant) {
    t38 = headerSections?.map(section_0 => <li key={section_0.id} onMouseEnter={() => handleSubsectionOpen(section_0.id)} onMouseLeave={handleSubsectionClose} onFocus={() => handleSubsectionOpen(section_0.id, {
      immediate: true
    })} onBlur={handleSubsectionClose}><Suspense>{section_0.subsection && <GcssSubsection isOpen={debouncedOpenedSubmenu === section_0.id} handleClick={() => {
          setOpenedSubmenu(value_0 => {
            if (value_0 === section_0.id) {
              return null;
            }
            return section_0.id;
          });
        }} sectionName={section_0.name} sectionId={section_0.id} key={section_0.id} subsection={section_0.subsection} isScrolling={scrolling} isSectionActive={false} variant={variant} />}</Suspense></li>);
    $[66] = debouncedOpenedSubmenu;
    $[67] = handleSubsectionClose;
    $[68] = handleSubsectionOpen;
    $[69] = headerSections;
    $[70] = scrolling;
    $[71] = setOpenedSubmenu;
    $[72] = variant;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  let t39;
  if ($[74] !== t38) {
    t39 = <ul className="no-scrollbar relative flex flex-row gap-5 overflow-x-auto px-4 xl:px-0" ref={refList}>{t38}</ul>;
    $[74] = t38;
    $[75] = t39;
  } else {
    t39 = $[75];
  }
  let t40;
  if ($[76] !== t35 || $[77] !== t37 || $[78] !== t39) {
    t40 = <div className="-mx-4 xl:hidden"><div className="relative">{t35}{t37}{t39}</div></div>;
    $[76] = t35;
    $[77] = t37;
    $[78] = t39;
    $[79] = t40;
  } else {
    t40 = $[79];
  }
  let t41;
  if ($[80] !== t32 || $[81] !== t33 || $[82] !== t40 || $[83] !== t8) {
    t41 = <header ref={ref} className={t8}>{t32}{t33}{t40}</header>;
    $[80] = t32;
    $[81] = t33;
    $[82] = t40;
    $[83] = t8;
    $[84] = t41;
  } else {
    t41 = $[84];
  }
  return t41;
}